/**
 * AuthContext — single source of truth for "who's logged in?".
 *
 * The cookie that backs the session is httpOnly, so the frontend can't
 * inspect it directly. Instead we hit `/auth/me` on mount; if it
 * returns 200 we're logged in, if 401 we're not. The result is cached
 * in context so the navbar, route guards, and callers all read from
 * the same place.
 *
 * `login` and `logout` mutate the cookie via the API; on success we
 * refresh the cached `me`. `signal` exposes a tiny version counter so
 * callers can `useEffect` against it and refetch their own data when
 * the user changes.
 */

import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AUTH_EPOCH_KEY, authGeneration, clearPrivateData } from "../lib/privateDataCache";

import { ApiError } from "../lib/api";
import {
  acceptInvite as apiAcceptInvite,
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  signup as apiSignup,
  type MeResponse,
  type SignupInput,
} from "../lib/auth";

interface AuthState {
  status: "loading" | "authenticated" | "anonymous";
  me: MeResponse | null;
  /** Increments on every login/logout — useful as a `useEffect` dep. */
  version: number;
  login: (email: string, password: string) => Promise<MeResponse>;
  /** Self-serve trial creation. Same cookie + state-update semantics
   *  as login on success — the user lands authenticated immediately. */
  signup: (input: SignupInput) => Promise<MeResponse>;
  /** Accept an admin-issued magic-link invite. Same cookie + state-
   *  update semantics as login on success — the user lands
   *  authenticated and can be redirected straight to /home. */
  acceptInvite: (token: string, password: string) => Promise<MeResponse>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [version, setVersion] = useState(0);
  const identity = useRef<string | null>(null);

  const clearSession = useCallback((broadcast = false) => {
    clearPrivateData(broadcast);
    if (identity.current !== null) setVersion((v) => v + 1);
    identity.current = null;
    setMe(null);
    setStatus("anonymous");
  }, []);

  const acceptSession = useCallback((next: MeResponse) => {
    const key = `${next.user.id}:${next.workspace.id}:${next.role}`;
    if (key !== identity.current) {
      clearPrivateData();
      identity.current = key;
      setVersion((v) => v + 1);
    }
    setMe(next);
    setStatus("authenticated");
  }, []);

  const refresh = useCallback(async () => {
    const generation = authGeneration();
    try {
      const next = await fetchMe();
      if (generation !== authGeneration()) return;
      // Guard against malformed responses (e.g. HTML redirect instead of JSON)
      if (!next || typeof next !== "object" || !("user" in next)) {
        clearSession();
        return;
      }
      acceptSession(next);
    } catch (err) {
      if (generation !== authGeneration()) return;
      if (err instanceof ApiError && err.status === 401) {
        clearSession();
      } else {
        // Network / 500 → leave the user in whatever state they were
        // before. We don't want a transient outage to log everyone out.
        setStatus((prev) => (prev === "loading" ? "anonymous" : prev));
      }
    }
  }, [acceptSession, clearSession]);

  useEffect(() => {
    clearPrivateData();
    void refresh();
  }, [refresh]);

  // Global 401 handler — any fetch that comes back with 401 fires
  // `meridian:unauthenticated` from `lib/api.ts`. We flip to anonymous
  // here so the next render of RequireAuth bounces to /login.
  useEffect(() => {
    function onUnauth() {
      clearSession();
    }
    function onStorage(event: StorageEvent) {
      if (event.key === AUTH_EPOCH_KEY) {
        clearSession();
        void refresh();
      }
    }
    window.addEventListener("meridian:unauthenticated", onUnauth);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("meridian:unauthenticated", onUnauth);
      window.removeEventListener("storage", onStorage);
    };
  }, [clearSession, refresh]);

  const login = useCallback(async (email: string, password: string) => {
    clearSession(true);
    const next = await apiLogin(email, password);
    clearPrivateData(true);
    acceptSession(next);
    return next;
  }, [acceptSession, clearSession]);

  const signup = useCallback(async (input: SignupInput) => {
    clearSession(true);
    const next = await apiSignup(input);
    clearPrivateData(true);
    acceptSession(next);
    return next;
  }, [acceptSession, clearSession]);

  const acceptInvite = useCallback(async (token: string, password: string) => {
    clearSession(true);
    const next = await apiAcceptInvite(token, password);
    clearPrivateData(true);
    acceptSession(next);
    return next;
  }, [acceptSession, clearSession]);

  const logout = useCallback(async () => {
    clearSession(true);
    try {
      await apiLogout();
    } finally {
      clearSession(true);
    }
  }, [clearSession]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      me,
      version,
      login,
      signup,
      acceptInvite,
      logout,
      refresh,
    }),
    [status, me, version, login, signup, acceptInvite, logout, refresh],
  );

  return <AuthCtx.Provider value={value}><Fragment key={version}>{children}</Fragment></AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/** Public previews may render outside AuthProvider while the same component is
 * authenticated in-product. Prefer this only for components that deliberately
 * support both modes. */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthCtx);
}
